import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { ModalContext } from "../../../components/Modal";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  getFiltersKeyValue,
  buildEnhancedFilterCriteria,
} from "../../../utils/util";
import { getLastBrackateValue } from "../../../utils/util";
import {
  FlatFilters,
  KeyMap,
} from "../../../interfaces/reviewSelectionInterfaces";
import { selectCurrentTab, setQuery } from "../../../redux/queryBuilderSlice";
import { Button } from "../../../components/button/Button";
import { showNotification } from "../../../redux/notificationSlice";
import CollapsibleFilers from "./CollapsibleFilers";
import Accordian from "../../../components/Accordian";
import Chip from "../../../components/Chip";
import { fetchMembersMembers } from "../../../redux/queryMetaSlice";
import logo from "../../../img/sp-load.svg";
import Loading from "../../../components/Loading";

const TIME_PERIODS = ["YEAR", "QUARTER", "MONTH"];

const ReviewSelection = () => {
  const dispatch: AppDispatch = useDispatch();
  const currTabData = useSelector(selectCurrentTab);

  const query = useSelector((state: RootState) => currTabData.query);

  const axesWithAllSelected = useSelector(
    (state: any) => currTabData.axesWithAllSelected,
  );

  const [filterData, setFilterData] = useState<FlatFilters>({});
  const [keyMap, setKeyMap] = useState<KeyMap>({});
  const [rows, setRows] = useState<string[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timePeriodsData, setTimePeriodsData] = useState([]);
  const [incExcMapData, setIncExcMap] = useState({});
  const [isRowlistOpen, setIsRowlistOpen] = useState(true);
  const [isColumnlistOpen, setIsColumnlistOpen] = useState(true);
  const [isFilterBoxOpen, setIsFilterBoxOpen] = useState(true);
  const [isAllSelectedMapData, setAllSelectedMapData] = useState<
    Record<string, any>
  >({});
  const [timePeriodsNode, setTimePeriodsNode] = useState<string[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [dragList, setDragList] = useState<"rows" | "columns" | null>(null);
  const modalContext = useContext(ModalContext);
  const close = modalContext?.close ?? (() => {});
  const currentTabIndex = useSelector(
    (state: any) => state.queryBuilder.currentTabIndex,
  );

  const metadataGroup = useSelector(
    (state: any) => state.meta.metaStates[currentTabIndex].metadataGroup,
  );
  const { dimensions = [] } = useSelector(
    (state: any) => state.meta.metaStates[currentTabIndex],
  );

  function findCaptionByUniqueName(targetUniqueName: string) {
    let result: any = null;
    if (checkIsMeasureAttr(targetUniqueName)) {
      return getLastBrackateValue(targetUniqueName);
    }
    function traverse(node: any) {
      if (!node || result) return;

      // Check current node
      if (node.uniqueName === targetUniqueName) {
        result = node.caption || node.name || null;
        return;
      }

      // Traverse hierarchies
      if (Array.isArray(node.hierarchies)) {
        node.hierarchies.forEach(traverse);
      }

      // Traverse levels
      if (Array.isArray(node.levels)) {
        node.levels.forEach(traverse);
      }
    }

    dimensions.forEach(traverse);
    return result.toUpperCase() || getLastBrackateValue(targetUniqueName);
  }

  useEffect(() => {
    if (metadataGroup?.length > 0) {
      let index = metadataGroup.findIndex(
        (item: any) => item.name == "Time Period",
      );
      if (index !== -1) {
        setTimePeriodsNode(metadataGroup[index].levels);
      }
    }
  }, [metadataGroup]);
  const handleReorderStart = (list: "rows" | "columns", index: number) => {
    if (list === "rows") setIsColumnlistOpen(true);
    else setIsRowlistOpen(true);
    setDragIndex(index);
    setDragList(list);
  };

  const checkIsMeasureAttr = (member: string) => {
    return member.split(".")[0] && member.split(".")[0] == "[Measures]"
      ? true
      : false;
  };

  const handleReorderEnter = (list: "rows" | "columns", index: number) => {
    if (dragList !== list || dragIndex === index) return;
    setHoverIndex(index);

    if (list === "rows") {
      setRows((prev) => {
        const newList = [...prev];
        const moved = newList.splice(dragIndex!, 1)[0];
        newList.splice(index, 0, moved);
        setDragIndex(index);
        return newList;
      });
    }

    if (list === "columns") {
      setColumns((prev) => {
        if (
          prev?.length > 0 &&
          checkIsMeasureAttr(prev[prev.length - 1]) &&
          index == prev.length - 1
        ) {
          // dispatch(
          //   showNotification({
          //     message: "Vehicle count would be always last",
          //     duration: 3000,
          //     isError: true,
          //   })
          // );
          return prev;
        } else {
          const newList = [...prev];
          const moved = newList.splice(dragIndex!, 1)[0];
          newList.splice(index, 0, moved);
          setDragIndex(index);
          return newList;
        }
      });
    }
  };

  const handleReorderEnd = () => {
    setDragIndex(null);
    setHoverIndex(null);
    setDragList(null);
  };

  const fetchData = async (uniquename: string) => {
    const data = await dispatch(fetchMembersMembers(uniquename));
    setTimePeriodsData(data.payload);
    setIsLoading(false);
  };

  useEffect(() => {
    if (timePeriodsNode?.length > 0) {
      setIsLoading(true);
      fetchData(timePeriodsNode[0]);
    }
  }, [dispatch, timePeriodsNode]);
  useEffect(() => {
    if (!query) return;
    const {
      flatFilter,
      keyMap: km,
      incExcMap,
      isAllSelectedMap,
    } = getFiltersKeyValue(
      query.enhancedFilterCriteria,
      axesWithAllSelected,
      timePeriodsData,
      timePeriodsNode,
    );
    setIncExcMap(incExcMap);
    setAllSelectedMapData(isAllSelectedMap);
    setFilterData(flatFilter);
    setKeyMap(km);
    setRows(query?.axes?.ROWS ?? []);
    setColumns(query?.axes?.COLUMNS ?? []);
  }, [query, timePeriodsData]);

  const originalState = useMemo(() => {
    const { flatFilter } = getFiltersKeyValue(
      query?.enhancedFilterCriteria,
      axesWithAllSelected,
      timePeriodsData,
      timePeriodsNode,
    );
    return JSON.stringify({
      filters: flatFilter,
      rows: query?.axes?.ROWS ?? [],
      columns: query?.axes?.COLUMNS ?? [],
    });
  }, [query, timePeriodsData]);

  useEffect(() => {
    const currentState = JSON.stringify({ filters: filterData, rows, columns });
    setIsDirty(currentState !== originalState);
  }, [filterData, rows, columns, originalState]);

  const handleRemoveFilter = (category: string, filterItem: any) => {
    if (category === "[Time Periods]" && filterData[category]?.length <= 1) {
      dispatch(
        showNotification({
          message:
            "Cannot remove. At least one filter is required for Time Periods",
          duration: 5000,
          isError: true,
        }),
      );
    } else {
      setFilterData((prev) => ({
        ...prev,
        [category]: prev[category].filter(
          (v) =>
            !(
              v.value === filterItem.value &&
              v.selType === filterItem.selType &&
              v.namedSet === filterItem.namedSet
            )
        ),
      }));
    }
  };

  const hasNamedSetFiltersForAttribute = (attributeUniqueName: string) => {
    const mappedCategory = Object.keys(keyMap).find(
      (category) => keyMap[category] === attributeUniqueName,
    );

    if (mappedCategory && Array.isArray(filterData[mappedCategory])) {
      return filterData[mappedCategory].some((item: any) => item?.namedSet);
    }

    const fallbackCategory = attributeUniqueName.split(".").pop() ?? "";
    if (fallbackCategory && Array.isArray(filterData[fallbackCategory])) {
      return filterData[fallbackCategory].some((item: any) => item?.namedSet);
    }

    return false;
  };

  const removeRow = (row: string) => {
    if (timePeriodsNode.includes(row)) {
      dispatch(
        showNotification({
          message: "Cannot remove Time Periods",
          duration: 3000,
          isError: true,
        }),
      );
    } else if (checkIsMeasureAttr(row)) {
      dispatch(
        showNotification({
          message: "Cannot remove Measures/Metrics",
          duration: 3000,
          isError: true,
        }),
      );
    } else if (hasNamedSetFiltersForAttribute(row)) {
      dispatch(
        showNotification({
          message: `Cannot remove ${findCaptionByUniqueName(row)}. Named set values are selected for this attribute.`,
          duration: 4000,
          isError: true,
        }),
      );
    } else {
      setRows((prev) => prev.filter((r) => r !== row));
    }
  };
  const removeColumn = (col: string) => {
    if (timePeriodsNode.includes(col)) {
      dispatch(
        showNotification({
          message: "Cannot remove Time Periods",
          duration: 3000,
          isError: true,
        }),
      );
    } else if (checkIsMeasureAttr(col)) {
      dispatch(
        showNotification({
          message: "Cannot remove Measures/Metrics",
          duration: 3000,
          isError: true,
        }),
      );
    } else if (hasNamedSetFiltersForAttribute(col)) {
      dispatch(
        showNotification({
          message: `Cannot remove ${findCaptionByUniqueName(col)}. Named set values are selected for this attribute.`,
          duration: 4000,
          isError: true,
        }),
      );
    } else {
      setColumns((prev) => prev.filter((c) => c !== col));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDropToRows = (e: React.DragEvent) => {
    e.preventDefault();
    const value = e.dataTransfer.getData("text/plain");
    const source = e.dataTransfer.getData("text/source") as "rows" | "columns";
    if (!source) return;

    if (!value || source === "rows") return;
    setRows((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setColumns((prev) => prev.filter((c) => c !== value));
    setDragList(null);
    setHoverIndex(null);
  };

  const handleDropToColumns = (e: React.DragEvent) => {
    e.preventDefault();
    const value = e.dataTransfer.getData("text/plain");
    const source = e.dataTransfer.getData("text/source") as "rows" | "columns";

    if (!source) return;

    if (!value || source === "columns") return;

    setColumns((prev) => (prev.includes(value) ? prev : [value, ...prev]));

    setRows((prev) => prev.filter((r) => r !== value));
    setDragList(null);
    setHoverIndex(null);
  };
  const handleSave = () => {
    const newEnhanced = buildEnhancedFilterCriteria(
      filterData,
      keyMap,
      incExcMapData,
      timePeriodsData,
      timePeriodsNode,
    );

    const newQuery = {
      ...query,
      axes: {
        ROWS: rows,
        COLUMNS: columns,
      },
      enhancedFilterCriteria: newEnhanced,
    };

    dispatch(setQuery({ query: newQuery, currentActiveTab: null }));
    close();
  };

  const dropZoneStyle: React.CSSProperties = {
    minHeight: 60,
    maxHeight: 75,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    padding: 8,
    border: "1px dashed transparent",
  };
  return (
    <div className="dialog-container review-selection">
      <div className="dialog-header">
        <div className="dialog-title">
          <h3>Review Your Selections</h3>
        </div>
      </div>

      <div
        style={{
          padding: "10px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          overflow: "auto",
          flex: 1,
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ width: "50%" }}>
            <Accordian
              isActive
              title="Rows"
              isOpen={isRowlistOpen}
              setIsOpen={setIsRowlistOpen}
            >
              <div
                onDragOver={handleDragOver}
                onDrop={handleDropToRows}
                style={{
                  ...dropZoneStyle,
                  borderColor: "#ccc",
                  borderRadius: 4,
                  background: "#fafafa",
                }}
              >
                {rows.length ? (
                  rows.map((row, index) => (
                    <Chip
                      key={row}
                      label={
                        TIME_PERIODS.includes(getLastBrackateValue(row))
                          ? "TIME PERIODS"
                          : findCaptionByUniqueName(row)
                      }
                      onRemove={() => removeRow(row)}
                      draggable={true}
                      dragValue={row}
                      dragSource="rows"
                      // NEW EVENTS FOR REORDER
                      onDragStart={() => handleReorderStart("rows", index)}
                      onDragEnter={() => handleReorderEnter("rows", index)}
                      onDragEnd={handleReorderEnd}
                      isHover={hoverIndex === index && dragList == "rows"}
                      isDragging={dragIndex === index && dragList == "rows"}
                    />
                  ))
                ) : (
                  <div style={{ padding: 6, color: "#666" }}>No Rows Found</div>
                )}
              </div>
            </Accordian>
          </div>

          <div style={{ width: "50%" }}>
            <Accordian
              isActive
              title="Columns"
              isOpen={isColumnlistOpen}
              setIsOpen={setIsColumnlistOpen}
            >
              <div
                onDragOver={handleDragOver}
                onDrop={handleDropToColumns}
                style={{
                  ...dropZoneStyle,
                  borderColor: "#ccc",
                  borderRadius: 4,
                  background: "#fafafa",
                }}
              >
                {columns.length ? (
                  columns.map((col, index) => (
                    <Chip
                      key={col}
                      label={
                        TIME_PERIODS.includes(getLastBrackateValue(col))
                          ? "TIME PERIODS"
                          : findCaptionByUniqueName(col)
                      }
                      onRemove={() => removeColumn(col)}
                      draggable={!checkIsMeasureAttr(col)}
                      dragValue={col}
                      dragSource="columns"
                      onDragStart={() => handleReorderStart("columns", index)}
                      onDragEnter={() => handleReorderEnter("columns", index)}
                      onDragEnd={handleReorderEnd}
                      isHover={hoverIndex === index && dragList == "columns"}
                      isDragging={dragIndex === index && dragList == "columns"}
                    />
                  ))
                ) : (
                  <div style={{ padding: 6, color: "#666" }}>
                    No Columns Found
                  </div>
                )}
              </div>
            </Accordian>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Accordian
            isActive
            title="Filters"
            isHeightFull
            isOpen={isFilterBoxOpen}
            setIsOpen={setIsFilterBoxOpen}
          >
            <div
              style={{
                overflow: "auto",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 5,
              }}
            >
              {isLoading ? (
                <Loading show={true} />
              ) : (
                Object.entries(filterData).map(
                  ([category, values]) =>
                    values.length > 0 &&
                    !isAllSelectedMapData[category] && (
                      <CollapsibleFilers
                        key={category}
                        title={category}
                        incExcMapData={incExcMapData}
                        filters={values}
                        axesWithAllSelected={axesWithAllSelected}
                        onRemoveFilter={handleRemoveFilter}
                      />
                    ),
                )
              )}

               <div style={{ display: "flex", alignItems: "center",justifyContent:"center", gap: 12,marginTop: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 24,
                height: 12,
                borderRadius: 6,
                background: "#e1e8f5",
                border: "1px solid #d1d5db",
                display: "inline-block",
              }}
            />

            <span style={{ fontSize: 12, color: "#374151" }}>
          Display Field
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6,  }}>
           <span
              style={{
                width: 24,
                height: 12,
                borderRadius: 6,
                background: "#f8f6d2",
                border: "1px solid #d1d5db",
                display: "inline-block",
              }}
            />
            <span style={{ fontSize: 12, color: "#374151" }}>Named Set</span>
          </div>

        
        </div>
            </div>
            
          </Accordian>
        </div>
        
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "0 20px 8px 20px",
        }}
      >
        
        <div
          style={{
            fontSize: 12,
            color: "#4b5563",
            background: "#E1F5FE",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            fontWeight: 600,
            padding: "6px 10px",
          }}
        >
          Note: Named set are always included in filter.
        </div>

       
      </div>



      <div className="dialog-footer">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button
            className="btn sm"
            color="primary"
            variant="outline"
            onClick={close}
          >
            CANCEL
          </Button>
          <Button
            className="btn sm"
            disabled={!isDirty}
            color="primary"
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewSelection;
